import React, { useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Creators as CarouselItemCreators } from 'store/carousel-items/carousel-items-duck.js';
import { resolveImageUrl } from 'utils/apiOrigin';

const ACCEPT = 'image/jpeg,image/png,image/webp';

const CarouselItemModal = ({
  isOpen,
  carouselToUpdate,
  carouselItemsCloseModal,
  carouselItemsCreateAsync,
  carouselItemsUpdateAsync,
}) => {
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { active: true } });

  // limpa o campo de arquivo ao abrir
  useEffect(() => {
    if (isOpen) {
      reset(carouselToUpdate);
      setValue('image', null);
    } else {
      reset();
    }
  }, [isOpen, carouselToUpdate, reset, setValue]);

  const fileList = watch('image');
  const pickedFile = useMemo(() => (fileList && fileList.length ? fileList[0] : null), [fileList]);

  const previewSrc = useMemo(() => {
    if (pickedFile) {
      return URL.createObjectURL(pickedFile);
    }
    if (carouselToUpdate?.imageLink) {
      return resolveImageUrl(carouselToUpdate.imageLink);
    }
    return null;
  }, [pickedFile, carouselToUpdate]);

  const onSubmit = async (data) => {
    const payload = {
      description: data.description,
      link: data.link,
      active: data.active,
      // o backend espera o campo "image" no multipart
      image: pickedFile || null,
    };

    if (carouselToUpdate?.id) {
      carouselItemsUpdateAsync({ id: carouselToUpdate.id, ...payload });
    } else {
      carouselItemsCreateAsync(payload);
    }
    carouselItemsCloseModal();
  };

  return (
    <Dialog open={isOpen} onClose={carouselItemsCloseModal} fullWidth maxWidth="sm">
      <DialogTitle>
        {carouselToUpdate?.id ? 'Editar Item do Carrossel' : 'Novo Item do Carrossel'}
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          label="Descrição"
          variant="filled"
          fullWidth
          margin="normal"
          error={!!errors.description}
          helperText={errors.description?.message}
          {...register('description', { required: 'A descrição é obrigatória' })}
        />

        <TextField
          label="Link"
          variant="filled"
          fullWidth
          margin="normal"
          error={!!errors.link}
          helperText={errors.link?.message}
          {...register('link', { required: 'O link é obrigatório' })}
        />

        {/* Preview da imagem atual ou escolhida */}
        {previewSrc && (
          <div style={{ marginTop: 8, marginBottom: 12 }}>
            <img
              src={previewSrc}
              alt="Pré-visualização"
              style={{ width: '100%', maxHeight: 240, objectFit: 'cover', borderRadius: 8 }}
            />
          </div>
        )}

        {/* Upload de arquivo (substitui o antigo campo "Link da imagem") */}

        <input
          type="file"
          accept={ACCEPT}
          {...register('image')}
          style={{ marginTop: 8, marginBottom: 8, width: '100%' }}
        />

        <Controller
          name="active"
          control={control}
          defaultValue={carouselToUpdate?.active ?? true}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              sx={{ mt: 1 }}
              control={<Switch checked={!!value} onChange={(e) => onChange(e.target.checked)} />}
              label={value ? 'Ativo' : 'Inativo'}
            />
          )}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={carouselItemsCloseModal} color="secondary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
          disabled={isSubmitting}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  isOpen: state.CarouselItem.isModalOpen,
  carouselToUpdate: state.CarouselItem.carouselToUpdate,
});

const mapDispatchToProps = (dispatch) => bindActionCreators(CarouselItemCreators, dispatch);

CarouselItemModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  carouselToUpdate: PropTypes.object,
  carouselItemsCloseModal: PropTypes.func.isRequired,
  carouselItemsCreateAsync: PropTypes.func.isRequired,
  carouselItemsUpdateAsync: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(CarouselItemModal);
